import MultiCustomersPicker from '@app/components/multi-customers-picker';
import MultiGoodsPicker from '@app/components/multi-goods-picker';
import MultiPeriodPicker from '@app/components/multi-period-picker';
import MultiSitesPicker from '@app/components/multi-sites-picker';
import MultiTagsPicker from '@app/components/multi-tag-picker';
import { useCountReportRecords } from '@shared/api/hooks/reports/reports';
import { OperationReportTypeEnum } from '@shared/api/types';
import Alert from '@shared/components/alert';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import InputFormField from '@shared/components/form/input-form-field';
import GuideTemplate from '@shared/components/guide-template';
import Loader from '@shared/components/loader';
import { Button } from '@shared/components/ui/button';
import sentry from '@shared/services/sentry';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  generatePath,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import { getReportsPathMap } from '../utils';
import { CreateReportContext } from './create-report-layout';

function useRecordCount(reportType: OperationReportTypeEnum) {
  const { mutateAsync: countRecords, isLoading: counting } =
    useCountReportRecords();
  const { watch } = useFormContext();
  const [count, setCount] = useState<number | undefined>(undefined);
  const [hasUnreviewedData, setHasUnreviewedData] = useState(false);

  const goodsTypes = watch('goodsTypes');
  const timePeriods = watch('timePeriods');
  const sites = watch('sites');
  const tags = watch('tags');
  const customers = watch('customers');

  const fetchCount = useCallback(async () => {
    try {
      const result = await countRecords({
        data: {
          goodsTypes,
          timePeriods,
          sites,
          tags,
          customers,
          type: reportType,
        },
      });
      setCount(result.count);
      setHasUnreviewedData(result.hasUnreviewedData);
    } catch (error) {
      sentry.log(error);
    }
  }, [
    countRecords,
    goodsTypes,
    timePeriods,
    sites,
    tags,
    customers,
    reportType,
  ]);

  useEffect(() => {
    fetchCount().catch((error) => sentry.log(error));
  }, [fetchCount]);

  return { count, counting, hasUnreviewedData };
}

export default function CreateReportSetupPage() {
  const { report_uuid } = useParams();
  const navigate = useNavigate();
  const { reportType, isEditing } = useOutletContext<CreateReportContext>();
  const { trigger } = useFormContext();
  const { count, counting, hasUnreviewedData } = useRecordCount(
    reportType as OperationReportTypeEnum
  );

  const onNext = async () => {
    const isValid = await trigger('title');
    if (!count || !isValid) return;

    return isEditing
      ? navigate(
          generatePath(getReportsPathMap()[reportType!].editMetric!, {
            report_uuid,
          })
        )
      : navigate(getReportsPathMap()[reportType!].metric!);
  };

  const onBack = () => {
    return isEditing
      ? navigate(getReportsPathMap()[reportType!].list!)
      : navigate(getReportsPathMap()[reportType!].create!);
  };

  return (
    <GuideTemplate
      preTitle={`${isEditing ? 'Edit' : 'New'} ${reportType} report`}
      title="Configure your report"
      description={
        <Text>
          To get started, add a name and select which {reportType}s you wish to
          include in your report. By default, all {reportType}s will be
          included.
        </Text>
      }
      primaryActionText="Next"
      secondaryActionText={isEditing ? 'Cancel' : 'Back'}
      primaryActionProps={{
        onClick: onNext,
        disabled: counting || !count,
        'data-testid': 'next-button',
      }}
      secondaryActionProps={{
        onClick: onBack,
      }}
    >
      <form className="text-left">
        <fieldset className="space-y-4">
          <InputFormField
            name="title"
            className="w-[450px] max-w-form"
            label="Report name"
            placeholder={`Q1 2024 ${reportType === OperationReportTypeEnum.purchase ? 'purchases' : 'sales'}`}
            data-testid="report-name-input"
          />
          <MultiGoodsPicker
            name="goodsTypes"
            className="w-[450px] max-w-form"
            label="Type of goods"
            multiSelectProps={{
              placeholder: `All ${reportType === OperationReportTypeEnum.purchase ? 'purchased' : 'sold'} goods included`,
            }}
          />
          <MultiPeriodPicker
            name="timePeriods"
            className="w-[450px] max-w-form"
            label="Time periods"
          />
          <MultiSitesPicker
            name="sites"
            className="w-[450px] max-w-form"
            label="Sites"
          />
          {reportType === OperationReportTypeEnum.sales && (
            <MultiCustomersPicker
              name="customers"
              className="w-[450px] max-w-form"
              label="Customers"
            />
          )}
          <MultiTagsPicker
            name="tags"
            className="w-[450px] max-w-form"
            label="Tags"
            placeholder="All tags included"
            disabledPlaceholder="No tags added"
          />
        </fieldset>
      </form>
      <div className="mt-6 flex w-full flex-row items-center justify-center space-x-2">
        <div className="size-4">
          {counting && <Loader size="xs" />}
          {!counting && !!count && (
            <Icons.check className="size-4 text-secondary" />
          )}
          {!counting && !count && <Icons.x className="size-4 text-orange" />}
        </div>
        <Text variant="body">
          {counting && `Counting ${reportType} records...`}
          {!counting &&
            !!count &&
            `Report will include ${count} ${reportType} record${count === 1 ? '' : 's'}.`}
          {!counting &&
            !count &&
            `No ${reportType} records included, try adjusting the filters`}
        </Text>
      </div>
      {hasUnreviewedData && (
        <Alert variant="warning" className="mt-6 self-center py-1">
          <div className="flex flex-row items-center justify-between space-x-2">
            <div className="flex items-center space-x-2">
              <Icons.warning className=" text-warning-700" size={16} />
              <Text>Includes items that needs review for accuracy</Text>
            </div>
            <Button
              variant="link"
              onClick={() =>
                navigate(
                  generatePath(getReportsPathMap()[reportType!].foodReview!)
                )
              }
              className="pr-0 text-warning-700"
            >
              Review
            </Button>
          </div>
        </Alert>
      )}
    </GuideTemplate>
  );
}
